import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import zeplinColor from 'theme/Color';
import { useEffect, useState } from 'react';
import { LeadsService } from 'service/leads.service';

type NewLeadTypeProps = {
  modalOpen: boolean;
  handleModalClose: () => void;
  handleContinueAdd: () => void;
  setTypeSelected: (type: string) => void;
  typeSelected: string;
  setTypeIdSelected: (id: string) => void;
};

const NewLeadType = ({
  modalOpen,
  handleModalClose,
  handleContinueAdd,
  setTypeSelected,
  typeSelected,
  setTypeIdSelected,
}: NewLeadTypeProps) => {
  const [leadTypes, setLeadTypes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLeadTypes = async () => {
      setLoading(true);
      try {
        const response = await LeadsService.getLeadTypes();
        console.log('Fetched lead types:', response);
        setLeadTypes(response || {});

        // Set first type as default
        const firstType = Object.keys(response || {})[0];
        if (firstType) {
          setTypeSelected(firstType);
          setTypeIdSelected(response[firstType]);
        }
      } catch (error) {
        console.error('Failed to fetch lead types:', error);
      } finally {
        setLoading(false);
      }
    };

    if (modalOpen) {
      fetchLeadTypes();
    }
  }, [modalOpen]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const typeName = event.target.value;
    setTypeSelected(typeName);
    setTypeIdSelected(leadTypes[typeName]);
  };

  return (
    <CustomModal open={modalOpen} handleClose={handleModalClose}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Box
          sx={{
            width: { xs: '350px', xl: '400px' },
            backgroundColor: zeplinColor.Neutral1,
            borderRadius: '16px',
            padding: { xs: '20px', xl: '24px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '20px', xl: '24px' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              Select Lead Type
            </Typography>
            <img
              src='/assets/close-icon.svg'
              alt='close'
              style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              onClick={handleModalClose}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FormControl component='fieldset'>
              <RadioGroup value={typeSelected} onChange={handleRadioChange}>
                {loading ? (
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                    Loading types...
                  </Typography>
                ) : Object.keys(leadTypes).length === 0 ? (
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                    No lead types available
                  </Typography>
                ) : (
                  Object.keys(leadTypes).map((typeName) => (
                    <FormControlLabel
                      key={leadTypes[typeName]}
                      value={typeName}
                      control={
                        <Radio
                          sx={{
                            color: zeplinColor.BasicBlack,
                            '&.Mui-checked': {
                              color: zeplinColor.BasicBlack,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                          {typeName}
                        </Typography>
                      }
                    />
                  ))
                )}
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', width: '100%' }}>
            <CustomButton
              onClick={handleContinueAdd}
              variant='contained'
              disabled={!typeSelected || loading}
              color='primary'
              style={{
                borderRadius: '14px',
                height: { xs: '40px', xl: '48px' },
                padding: '14px 16px',
                width: '100%',
              }}
              textStyle={{ color: zeplinColor.Neutral1 }}
              text='Continue'
            />
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default NewLeadType;
